import JSZip from 'jszip';
import { fetchFileAsBlob, triggerBlobDownload } from './downloadFile';

// zip に含めるファイルの最小要件（ProjectMessageFile / MediaItem 等が構造的に満たす）
export type ZipFileEntry =
  | {
      // zip 内の相対パス（例: "サブフォルダ/ファイル名.png"）
      path: string;
      file: {
        id?: string;
        original_url: string;
        // ダウンロード用 presigned URL（存在する場合は original_url より優先）
        download_url?: string | null;
        // バイト数（分かる場合のみ。メモリ上で zip 化できる大きさかの判定に使う）
        size?: number | null;
      };
    }
  | {
      path: string;
      // 生成済みコンテンツをそのまま格納する（MA タイムチャート CSV 等）
      blob: Blob;
    };

export interface DownloadFilesResult {
  total: number;
  succeeded: number;
  failed: number;
  // ダウンロードに成功したファイルの ID 一覧（既読登録用）
  succeededFileIds: string[];
}

export interface DownloadFilesOptions {
  // 1ファイル処理（成功/失敗）するたびに呼ばれる（処理済み件数, 総数）
  onProgress?: (processed: number, total: number) => void;
}

// ファイル名・フォルダ名として使えない文字を置換する（zip 名 / zip 内・保存先のパスの各階層）
export const sanitizeFileName = (name: string): string =>
  name.replace(/[\\/:*?"<>|]/g, '_').trim() || 'download';

// 同時取得数。S3 等への過負荷とブラウザのメモリ圧迫を避けるため適度に制限する。
const CONCURRENCY = 4;

/**
 * 指定されたファイル一覧を zip にまとめてダウンロードする。
 *
 * 各ファイルの original_url を取得して JSZip に追加し、無圧縮(STORE)で zip 化して保存する。
 * メディアファイル（画像・動画・音声）は既に圧縮済みのため、STORE が高速かつ低負荷。
 * 取得に失敗したファイルはスキップし、成功/失敗件数を返す（呼び出し側で通知する）。
 *
 * @param entries zip 内パス付きのファイル一覧
 *   （flattenFolderToPaths / flattenMessageFilesToPaths の戻り値、または MediaItem から構築）
 * @param zipName 保存する zip ファイル名（例: "フォルダ名.zip"）
 */
export async function downloadFilesAsZip(
  entries: ZipFileEntry[],
  zipName: string,
  options?: DownloadFilesOptions,
): Promise<DownloadFilesResult> {
  const zip = new JSZip();
  const total = entries.length;
  const succeededFileIds: string[] = [];
  let succeeded = 0;
  let failed = 0;
  let processed = 0;

  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < entries.length) {
      const entry = entries[cursor];
      cursor += 1;
      try {
        const blob = 'blob' in entry
          ? entry.blob
          : await fetchFileAsBlob(entry.file.download_url || entry.file.original_url);
        zip.file(entry.path, blob);
        succeeded += 1;
        if ('file' in entry && entry.file.id) {
          succeededFileIds.push(entry.file.id);
        }
      } catch {
        failed += 1;
      } finally {
        processed += 1;
        options?.onProgress?.(processed, total);
      }
    }
  };

  const workers = Array.from(
    { length: Math.min(CONCURRENCY, entries.length) },
    () => worker(),
  );
  await Promise.all(workers);

  // 1件も取得できなければ zip を生成しない
  if (succeeded === 0) {
    return { total, succeeded, failed, succeededFileIds };
  }

  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'STORE',
  });
  triggerBlobDownload(zipBlob, sanitizeFileName(zipName));

  return { total, succeeded, failed, succeededFileIds };
}
